import { createServerFn } from '@tanstack/react-start';
import { getRequest } from '@tanstack/react-start/server';

import { getUserPortfolioTransactionsById } from '~/database/supabase/portfolio/queries.server.ts';
import type { PortfolioForTransactionsType } from '~/database/supabase/portfolio/types.ts';
import { getHints } from '~/utils/client-hints.tsx';

import { requireStartAuthSession } from '../auth/require-start-auth-session.ts';
import { createServerFnObservabilityMiddleware } from '../utils/create-instrumented-server-fn.ts';
import { measureServerTiming } from '../utils/server-timing.ts';

export interface PortfolioTransactionsPageData {
	readonly activePortfolio: PortfolioForTransactionsType | null;
	readonly activePortfolioId: number;
	readonly userLocale: string;
}

function getEmptyPortfolioTransactionsPageData(portfolioId: number, userLocale: string): PortfolioTransactionsPageData {
	return {
		activePortfolio: null,
		activePortfolioId: portfolioId,
		userLocale,
	};
}

export const getPortfolioTransactionsPageData = createServerFn({ method: 'GET' })
	.middleware([
		createServerFnObservabilityMiddleware({
			dependencies: ['auth.session', 'supabase.portfolio.transactions'],
			relatesTo: 'portfolio.transactions',
		}),
	])
	.inputValidator((data: { portfolioId: number }) => data)
	.handler(async ({ data }) => {
		const request = getRequest();
		const authSession = await measureServerTiming({
			description: 'auth.session',
			name: 'portfolio-transactions-auth',
			run: () => requireStartAuthSession({ request }),
		});
		const userLocale = getHints(request).locale;
		const portfolioId = Number.isFinite(data.portfolioId) ? data.portfolioId : -1;

		if (portfolioId < 1) {
			return getEmptyPortfolioTransactionsPageData(portfolioId, userLocale);
		}

		const activePortfolio = await measureServerTiming({
			description: 'supabase.portfolio.transactions',
			name: 'portfolio-transactions-db',
			run: () =>
				getUserPortfolioTransactionsById({
					portfolioId,
					userId: authSession.userId,
				}),
		});

		if (!activePortfolio) {
			return getEmptyPortfolioTransactionsPageData(portfolioId, userLocale);
		}

		return {
			activePortfolio,
			activePortfolioId: portfolioId,
			userLocale,
		} satisfies PortfolioTransactionsPageData;
	});
